/**
 * Learning Paths Landing Client - Client-side wrapper for learning paths list
 * Fetches user enrollment data and renders learning path cards with enrollment functionality
 */
'use client';

import LearningPathCard from '@/components/common/learning-path-card';
import { useHasPartnerTag } from '@/hooks/useHasPartnerTag';
import { useUserLearningPaths } from '@/hooks/useUserLearningPaths';
import { getCoursesWithQuizCount, getVideosCountFromCourses } from '@/lib/utils/learning-path-counts';
import { getDuration, getTotalCourseDurationSeconds } from '@/lib/utils/duration';
import { learningPathShowsCertificatePromo } from '@/lib/utils/learning-path-utils';

/** Course shape expected by getVideosCountFromCourses / getCoursesWithQuizCount */
type LearningPathCoursesPayload = {
  course?: Array<{
    chapters?: Array<{
      lessons?: {
        lessons?: Array<{
          video_lesson?: unknown[];
          is_quiz?: boolean;
          quiz?: { question_count?: number; assessment?: unknown[] };
        }>;
      };
    }>;
  }>;
};

interface LearningPath {
  uid: string;
  url: string;
  title?: string;
  is_partner_specific?: boolean;
  is_assessment?: boolean;
  learning_pathway_details?: {
    thumbnail?: { url: string };
    headline?: string;
    description?: string;
    awards_certifications?: string | boolean;
    videos_count?: number;
  };
  courses?: LearningPathCoursesPayload;
}

interface LearningPathsLandingClientProps {
  /** Learning paths array, or single object when getEntryData returns 1 entry */
  learningPaths: LearningPath[] | LearningPath;
}

/** Normalize learning paths to array - Contentstack may return single object when 1 entry */
function toPathsArray(paths: LearningPath[] | LearningPath): LearningPath[] {
  return Array.isArray(paths) ? paths : paths ? [paths] : [];
}

/** Normalize courses payload - Contentstack may return course as single object when 1 item */
function normalizeCourses(courses: LearningPathCoursesPayload | undefined): LearningPathCoursesPayload | undefined {
  if (!courses?.course) return courses;
  const courseList = Array.isArray(courses.course) ? courses.course : [courses.course];
  return { course: courseList };
}

export default function LearningPathsLandingClient({
  learningPaths,
}: LearningPathsLandingClientProps) {
  const { enrolledMap } = useUserLearningPaths();
  const pathsArray = toPathsArray(learningPaths);
  // Partner-specific cards only when logged in AND has partner (impartner) tag; non-logged-in
  // and still-resolving both read as "can't show" here (`=== true` below), same as before.
  const canShowPartnerPaths = useHasPartnerTag() === true;

  return (
    <>
      {pathsArray.map((path: LearningPath) => {
        const { isSubscribed = false, hasStarted = false, progressPercent, isCompleted, isLearningMaterialCompleted } =
          enrolledMap.get(path.uid) || {};
        const courses = normalizeCourses(path?.courses);

        const coursesCount = (courses?.course?.length ?? 0) > 0 ? courses?.course?.length : undefined;

        const totalDurationSeconds = (courses?.course ?? []).reduce(
          (sum, c) => sum + getTotalCourseDurationSeconds(c as Parameters<typeof getTotalCourseDurationSeconds>[0]),
          0,
        );
        const duration = totalDurationSeconds > 0 ? getDuration(totalDurationSeconds) : undefined;

        const videosCount =
          path?.learning_pathway_details?.videos_count != null &&
          path.learning_pathway_details.videos_count > 0
            ? path.learning_pathway_details.videos_count
            : courses
              ? getVideosCountFromCourses(courses)
              : 0;
        const quizzesCount = courses ? getCoursesWithQuizCount(courses) : 0;

        return path?.is_partner_specific && !canShowPartnerPaths ? null : (
          <div key={path?.uid} className="mb-4">
            <LearningPathCard
              uid={path?.uid}
              url={`learning-paths${path?.url}`}
              thumbnail={path?.learning_pathway_details?.thumbnail?.url}
              title={path?.learning_pathway_details?.headline || path?.title || ''}
              description={path?.learning_pathway_details?.description || ''}
              hasCertificate={learningPathShowsCertificatePromo(
                path?.learning_pathway_details?.awards_certifications
              )}
              coursesCount={coursesCount}
              videosCount={videosCount > 0 ? videosCount : undefined}
              quizzesCount={quizzesCount > 0 ? quizzesCount : undefined}
              assessmentCount={path?.is_assessment ? 1 : undefined}
              duration={duration}
              isEnrolled={isSubscribed}
              hasStarted={hasStarted}
              progressPercent={progressPercent}
              isCompleted={isCompleted}
              isLearningMaterialCompleted={isLearningMaterialCompleted}
              hasAssessment={path?.is_assessment === true}
            />
          </div>
        );
      })}
    </>
  );
}
